import { Injectable } from '@angular/core';

/**
 * Single source of truth for the current user's roles / permissions.
 *
 * SECURITY: authorization state is DERIVED IN MEMORY from the signed JWT
 * (`xs_access_token`) rather than read from separately-forgeable localStorage
 * keys (`user-permissions`, `feature_permissions`, `user_type`, `admin`).
 * Tampering with those keys does nothing because nothing reads them; tampering
 * with the token invalidates its signature so the backend rejects every call.
 * NOTE: this is UI-gating only — the backend must still enforce authorization
 * per request. See the VAPT remediation notes.
 *
 * Exposed both as free functions (for components — Angular 12 has no field
 * `inject()`) and as an injectable service (for guards / effects).
 */

let cachedToken: string | null = null;
let cachedPayload: any = null;

/** Decoded JWT payload for the current token, or null if not signed in. */
function currentPayload(): any {
  const token = localStorage.getItem('xs_access_token');
  if (!token) {
    cachedToken = null;
    cachedPayload = null;
    return null;
  }
  if (token !== cachedToken) {
    cachedToken = token;
    cachedPayload = decodeJwt(token);
  }
  return cachedPayload;
}

/** A merchant-owner token carries only merchant_id (no user_id) => unrestricted. */
export function isMerchantOwner(): boolean {
  const p = currentPayload();
  return !!p && !p.user_id;
}

export function getUserType(): 'user' | 'merchant' | null {
  const p = currentPayload();
  if (!p) return null;
  return p.user_id ? 'user' : 'merchant';
}

/** Assigned role name from the JWT (e.g. "All Permissions"); '' for a merchant-owner. */
export function getUserRoleName(): string {
  const p = currentPayload();
  return p?.roles?.[0]?.name || '';
}

/**
 * Sub-user role-permission objects (replaces the old `user-permissions` key).
 * Returns null for a merchant-owner, which callers treat as full access.
 */
export function getUserPermissions(): any[] | null {
  const p = currentPayload();
  if (!p || !p.user_id) return null;
  return getUniquePermissions(p.roles || []);
}

/** Feature-permission matrix (replaces the old `feature_permissions` key). */
export function getFeaturePermissions(): any {
  const p = currentPayload();
  if (!p) return {};
  if (!p.user_id) return fullAccessFeaturePermissions();
  return generateFeaturePermissions(getUniquePermissions(p.roles || []));
}

// ---- derivation helpers (moved out of AuthEffects; single source) ----

export function decodeJwt(token: string): any {
  try {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function getUniquePermissions(roles: any[]): any[] {
  const uniquePermissionsMap = new Map<string, any>();

  roles.forEach((role) => {
    role.permissions.forEach((permission) => {
      Object.keys(permission).forEach((key) => {
        if (!uniquePermissionsMap.has(key)) {
          uniquePermissionsMap.set(key, permission[key]);
        }
      });
    });
  });

  return Array.from(uniquePermissionsMap.entries()).reduce((acc, [key, value]) => {
    acc.push({ [key]: value });
    return acc;
  }, []);
}

export function fullAccessFeaturePermissions(): any {
  return {
    product: ['all'],
    payment: ['all'],
    staff: ['all'],
    union: ['all'],
    dev: ['all'],
    customer: ['all'],
    transaction: ['all'],
    disbursement: ['all']
  };
}

export function generateFeaturePermissions(userPermissions: any[]): any {
    const features: any = {
      product: [],
      payment: [],
      staff: [],
      union: [],
      dev: [],
      customer: [],
      disbursement: []
    };

    userPermissions.forEach((permissionObj) => {
      // Handle Disbursement
      if (permissionObj.Disbursement) {
        const disburse = permissionObj.Disbursement;

        if (disburse['manage information'] === 1) {
          features.disbursement = ['all'];
        } else {
          if (disburse['approve_disbursement'] === 1 && !features.disbursement.includes('approve')) {
            features.disbursement.push('approve');
          }
          if (disburse['create_disbursement'] === 1 && !features.disbursement.includes('create')) {
            features.disbursement.push('create');
          }
          if (disburse['view reports'] === 1 && !features.disbursement.includes('view')) {
            features.disbursement.push('view');
          }
          if (disburse['export reports'] === 1 && !features.disbursement.includes('export')) {
            features.disbursement.push('export');
          }
          if (disburse['review_disbursement'] === 1 && !features.disbursement.includes('review')) {
            features.disbursement.push('review');
          }
          if (disburse['view information'] === 1 && features.disbursement.length === 0) {
            features.disbursement.push('view');
          }
          if (disburse['delete_recipient'] === 1 && !features.disbursement.includes('delete_recipient')) {
            features.disbursement.push('delete_recipient');
          }
          if (disburse['recommend_recipients'] === 1 && !features.disbursement.includes('recommend_recipients')) {
            features.disbursement.push('recommend_recipients');
          }
          if (disburse['reject_recipients'] === 1 && !features.disbursement.includes('reject_recipients')) {
            features.disbursement.push('reject_recipients');
          }
          if (disburse['verify_approval'] === 1 && !features.disbursement.includes('verify_approval')) {
            features.disbursement.push('verify_approval');
          }
          if (disburse['delete_draft'] === 1 && !features.disbursement.includes('delete_draft')) {
            features.disbursement.push('delete_draft');
          }
        }

        // TEMPORARY WORKAROUND: Also add to customer array for route guard compatibility
        // Remove this when backend adds disbursement to merchant feature_permissions
        if (features.disbursement.length > 0) {
          features.customer = features.customer.length > 0 ? features.customer : [];
          features.disbursement.forEach(perm => {
            if (!features.customer.includes(perm)) {
              features.customer.push(perm);
            }
          });
        }
      }

      // Handle Payment
      if (permissionObj.Payment) {
        const payment = permissionObj.Payment;
        if (payment['manage information'] === 1) {
          features.payment = ['all'];
        } else {
          if (payment['bulk_pay'] === 1) features.payment.push('bulk_pay');
          if (payment['move_to_bank'] === 1) features.payment.push('move_to_bank');
          if (payment['bulk_top_up'] === 1) features.payment.push('bulk_top_up');
          if (payment['top_up'] === 1) features.payment.push('top_up');
          if (payment['make_payment'] === 1) features.payment.push('make_payment');
        }
      }

      // Handle Product
      if (permissionObj.Products) {
        if (permissionObj.Products['manage information'] === 1) {
          features.product = ['all'];
        }
      }

      // Handle Staff
      if (permissionObj.Staff) {
        if (permissionObj.Staff['manage information'] === 1) {
          features.staff = ['all'];
        }
      }

      // Handle Union
      if (permissionObj.Union) {
        if (permissionObj.Union['manage information'] === 1) {
          features.union = ['all'];
        }
      }

      // Handle Dev/Api
      if (permissionObj['Dev/Api']) {
        if (permissionObj['Dev/Api']['manage information'] === 1) {
          features.dev = ['all'];
        }
      }

      // Handle Customers
      if (permissionObj.Customers) {
        if (permissionObj.Customers['manage information'] === 1) {
          features.customer = ['all'];
        }
      }
    });

    return features;
}

/** Injectable wrapper delegating to the free functions above (for guards / effects). */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  decodeJwt = decodeJwt;
  isMerchantOwner = isMerchantOwner;
  getUserType = getUserType;
  getUserPermissions = getUserPermissions;
  getFeaturePermissions = getFeaturePermissions;
  getUniquePermissions = getUniquePermissions;
  generateFeaturePermissions = generateFeaturePermissions;
  fullAccessFeaturePermissions = fullAccessFeaturePermissions;
}
